import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import EuroRoundedIcon from '@mui/icons-material/EuroRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import DashboardPageHeader from 'pages/dashboard/DashboardPageHeader';
import VendorDashboardLayout from 'pages/dashboard/VendorDashboardLayout';
import StatsChart from './StatsChart';
import OrderInsights from './OrderInsights';
import {
  formatEuro,
  formatPeriodLabel,
  getPresetRange,
  isFuturePeriod,
  isValidRange
} from './statsUtils';

const periodOptions = [
  { value: 'day', label: 'Днес' },
  { value: 'week', label: 'Тази седмица' },
  { value: 'month', label: 'Този месец' },
  { value: 'year', label: 'Тази година' },
  { value: 'custom', label: 'Избран период' }
];

const groupOptions = [
  { value: 'day', label: 'По дни' },
  { value: 'week', label: 'По седмици' },
  { value: 'month', label: 'По месеци' },
  { value: 'year', label: 'По години' }
];

const createInitialFilters = () => ({
  period: 'month',
  groupBy: 'day',
  ...getPresetRange('month')
});

const summaryCards = [
  { key: 'orders', label: 'Поръчки', icon: ReceiptLongRoundedIcon, color: '#4f46e5' },
  {
    key: 'revenueEur',
    label: 'Приходи (без доставка)',
    icon: EuroRoundedIcon,
    color: '#0f766e',
    money: true
  },
  {
    key: 'averageOrderEur',
    label: 'Средна поръчка (без доставка)',
    icon: TrendingUpRoundedIcon,
    color: '#b45309',
    money: true
  }
];

export default function Stats() {
  const initialFilters = useMemo(createInitialFilters, []);
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('timeline');
  const [breakdown, setBreakdown] = useState(null);
  const [isBreakdownLoading, setIsBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState('');
  const validRange = isValidRange(draftFilters);

  useEffect(() => {
    let isCurrent = true;

    const loadStats = async () => {
      setIsLoading(true);
      setError('');
      try {
        const { data } = await axios.get('/stats/orders', {
          params: { from: filters.from, to: filters.to, groupBy: filters.groupBy }
        });
        if (isCurrent) setReport(data);
      } catch (requestError) {
        if (isCurrent) {
          setError('Статистиката не можа да бъде заредена. Моля, опитайте отново.');
        }
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };

    loadStats();
    return () => {
      isCurrent = false;
    };
  }, [filters]);

  useEffect(() => {
    if (activeTab !== 'breakdown') return undefined;
    let isCurrent = true;

    const loadBreakdown = async () => {
      setIsBreakdownLoading(true);
      setBreakdownError('');
      try {
        const { data } = await axios.get('/stats/order-breakdown', {
          params: { from: filters.from, to: filters.to }
        });
        if (isCurrent) setBreakdown(data);
      } catch (requestError) {
        if (isCurrent) {
          setBreakdownError('Анализът на поръчките не можа да бъде зареден.');
        }
      } finally {
        if (isCurrent) setIsBreakdownLoading(false);
      }
    };

    loadBreakdown();
    return () => {
      isCurrent = false;
    };
  }, [activeTab, filters]);

  const selectPeriod = (period) => {
    setDraftFilters((current) => ({
      ...current,
      period,
      ...(period === 'custom' ? {} : getPresetRange(period))
    }));
  };

  const selectDate = (field, value) => {
    setDraftFilters((current) => ({ ...current, period: 'custom', [field]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    if (!validRange) return;
    setFilters({ ...draftFilters });
  };

  const rows = report?.data || [];
  const breakdownRows = rows.filter(
    (row) => !isFuturePeriod(row.period, report?.range?.groupBy || filters.groupBy)
  );
  const summary = report?.summary || { orders: 0, revenueEur: 0, averageOrderEur: 0 };

  return (
    <VendorDashboardLayout>
      <DashboardPageHeader
        title="Статистика"
        subtitle="Проследявайте поръчките и приходите от продукти в евро. Доставката не участва в изчисленията."
        icon={AssessmentRoundedIcon}
      />

      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 2.5 }}>
        <Tabs
          value={activeTab}
          onChange={(_event, value) => setActiveTab(value)}
          aria-label="Вид статистика"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="timeline" label="Поръчки и приходи" />
          <Tab value="breakdown" label="Анализ на поръчките" />
        </Tabs>
      </Box>

      <Card component="form" onSubmit={applyFilters} sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 }, '&:last-child': { pb: { xs: 2.5, md: 3 } } }}>
          <Stack direction="row" alignItems="center" gap={1} mb={2.5}>
            <CalendarMonthRoundedIcon color="primary" />
            <Typography variant="h6">Филтри</Typography>
          </Stack>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={6} lg={activeTab === 'timeline' ? 2.5 : 3}>
              <FormControl fullWidth>
                <InputLabel id="stats-period-label">Период</InputLabel>
                <Select
                  labelId="stats-period-label"
                  label="Период"
                  value={draftFilters.period}
                  onChange={(event) => selectPeriod(event.target.value)}
                >
                  {periodOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {activeTab === 'timeline' && (
              <Grid item xs={12} sm={6} lg={2.5}>
                <FormControl fullWidth>
                  <InputLabel id="stats-group-label">Групиране</InputLabel>
                  <Select
                    labelId="stats-group-label"
                    label="Групиране"
                    value={draftFilters.groupBy}
                    onChange={(event) =>
                      setDraftFilters((current) => ({
                        ...current,
                        groupBy: event.target.value
                      }))
                    }
                  >
                    {groupOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} lg={activeTab === 'timeline' ? 2.5 : 3}>
              <TextField
                fullWidth
                type="date"
                label="От дата"
                value={draftFilters.from}
                onChange={(event) => selectDate('from', event.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!validRange}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={activeTab === 'timeline' ? 2.5 : 3}>
              <TextField
                fullWidth
                type="date"
                label="До дата"
                value={draftFilters.to}
                onChange={(event) => selectDate('to', event.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!validRange}
              />
            </Grid>
            <Grid item xs={12} lg={activeTab === 'timeline' ? 2 : 3}>
              <Button fullWidth type="submit" variant="contained" disabled={!validRange}>
                Покажи
              </Button>
            </Grid>
          </Grid>
          {!validRange && (
            <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
              Началната дата трябва да бъде преди крайната дата.
            </Typography>
          )}
        </CardContent>
      </Card>

      {activeTab === 'timeline' && error && (
        <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>
      )}
      {activeTab === 'breakdown' && breakdownError && (
        <Alert severity="error" sx={{ mb: 2.5 }}>{breakdownError}</Alert>
      )}

      {activeTab === 'timeline' && (isLoading ? (
        <Card sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: 360 }}>
          <CircularProgress aria-label="Зареждане на статистиката" />
        </Card>
      ) : !error && report ? (
        <>
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            {summaryCards.map((item) => (
              <Grid item xs={12} sm={4} key={item.key}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Stack direction="row" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          alignItems: 'center',
                          bgcolor: `${item.color}12`,
                          borderRadius: 2.5,
                          color: item.color,
                          display: 'flex',
                          height: 46,
                          justifyContent: 'center',
                          width: 46
                        }}
                      >
                        <item.icon />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {item.label}
                        </Typography>
                        <Typography variant="h5">
                          {item.money ? formatEuro(summary[item.key]) : summary[item.key]}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={6}>
              <StatsChart
                title="Поръчки"
                description="Брой направени поръчки"
                rows={rows}
                groupBy={report.range.groupBy}
                valueKey="orders"
                color="#4f46e5"
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <StatsChart
                title="Приходи без доставка"
                description="Обща стойност на продуктите в EUR; таксата за доставка е изключена"
                rows={rows}
                groupBy={report.range.groupBy}
                valueKey="revenueEur"
                color="#0f766e"
                money
              />
            </Grid>
          </Grid>

          <TableContainer component={Card} sx={{ mt: 2.5 }}>
            <Box sx={{ p: 3, pb: 1 }}>
              <Typography variant="h6">Разбивка по период</Typography>
              <Typography variant="body2" color="text.secondary">
                Точните стойности от графиките. Приходите и средната поръчка не включват доставка.
              </Typography>
            </Box>
            <Table aria-label="Статистика по период">
              <TableHead>
                <TableRow>
                  <TableCell>Период</TableCell>
                  <TableCell align="right">Поръчки</TableCell>
                  <TableCell align="right">Приходи без доставка (EUR)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {breakdownRows.map((row) => (
                  <TableRow key={row.period}>
                    <TableCell>{formatPeriodLabel(row.period, report.range.groupBy, true)}</TableCell>
                    <TableCell align="right">{row.orders}</TableCell>
                    <TableCell align="right">{formatEuro(row.revenueEur)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null)}

      {activeTab === 'breakdown' && (isBreakdownLoading ? (
        <Card sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: 360 }}>
          <CircularProgress aria-label="Зареждане на анализа на поръчките" />
        </Card>
      ) : !breakdownError && breakdown ? (
        <OrderInsights data={breakdown} />
      ) : null)}
    </VendorDashboardLayout>
  );
}
